'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Package, CheckCircle2, Clock, Truck, FileText, ChevronRight } from 'lucide-react';
import type { Order } from '@/data/mockData';
import { useCommerce } from '@/context/CommerceContext';
import { fetchLiveOrder } from '@/services/api';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenInvoice: (order: Order) => void;
}

export default function OrderTrackingModal({ isOpen, onClose, onOpenInvoice }: OrderTrackingModalProps) {
  const { orders } = useCommerce();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeOrder, setActiveOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  React.useEffect(() => {
    if (!activeOrder && orders && orders.length > 0) {
      setActiveOrder(orders[0]);
      setSearchQuery(orders[0].orderNumber);
    }
  }, [orders, activeOrder]);

  if (!isOpen) return null;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setLoading(true);
    setErrorMsg('');

    const localMatch = orders.find(
      o => o.orderNumber.toLowerCase() === searchQuery.trim().toLowerCase() ||
           o.customerPhone.includes(searchQuery.trim())
    );
    if (localMatch) {
      setActiveOrder(localMatch);
      setLoading(false);
      return;
    }

    try {
      const live = await fetchLiveOrder(searchQuery.trim());
      if (live) {
        setActiveOrder(live);
        setErrorMsg('');
      } else {
        setErrorMsg('Order not found. Please verify the order number or mobile number.');
      }
    } catch {
      setErrorMsg('Unable to retrieve tracking info at this time.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-[#FFFFFF] rounded-3xl border-2 border-[#E09F55] shadow-2xl p-6 sm:p-8 text-[#171711] my-8"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-[#171711] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <span className="text-xs font-bold text-[#387613] uppercase tracking-wider block">
            Live Fulfillment Tracker • Order Status
          </span>
          <h3 className="font-display text-2xl font-bold text-[#950E09]">
            Track Your Food Order & Consignment
          </h3>
          <p className="text-xs text-[#171711]/70 font-sans">
            Real-time status tracking from our central kitchen to your doorstep.
          </p>
        </div>

        {/* Search Input */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-6 font-sans">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Enter Order ID (e.g. EF-ORD-9021) or Mobile..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#DDDAC4] bg-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#950E09]"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-2.5 rounded-xl bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] font-bold text-xs uppercase tracking-wider shadow cursor-pointer font-sans"
          >
            Track Order
          </button>
        </form>

        {errorMsg && (
          <div className="p-3 mb-4 rounded-xl bg-red-100 text-red-700 text-xs font-medium font-sans">
            {errorMsg}
          </div>
        )}

        {activeOrder && (
          <div className="space-y-6 font-sans">
            
            {/* Order Overview Banner */}
            <div className="p-4 rounded-2xl bg-white border border-[#DDDAC4] flex items-center justify-between">
              <div>
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-sans">Order Reference:</span>
                <div className="font-display font-black text-lg text-[#950E09]">{activeOrder.orderNumber}</div>
                <div className="text-xs text-gray-600 font-sans">Billed to: <strong>{activeOrder.customerName}</strong></div>
              </div>

              <div className="text-right">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider font-sans ${
                  activeOrder.orderStatus === 'DELIVERED'
                    ? 'bg-[#387613]/20 text-[#387613]'
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {activeOrder.orderStatus}
                </span>
                <div className="font-display font-bold text-sm text-[#171711] mt-1">₹{activeOrder.totalAmount}</div>
              </div>
            </div>

            {/* Stepper Timeline */}
            <div className="p-5 rounded-2xl bg-white border border-[#DDDAC4] space-y-4">
              <h4 className="font-display font-bold text-xs uppercase tracking-wider text-[#950E09]">
                Live Kitchen & Logistics Timeline
              </h4>

              <div className="space-y-4 relative before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-[#DDDAC4]">
                {activeOrder.trackingSteps?.map((step, sIdx) => (
                  <div key={sIdx} className="flex items-start space-x-3.5 relative">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 z-10 ${
                      step.done
                        ? 'bg-[#387613] text-white ring-4 ring-[#387613]/10'
                        : 'bg-white border-2 border-gray-300 text-gray-400'
                    }`}>
                      {step.done ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-bold ${step.current ? 'text-[#950E09]' : 'text-[#171711]'}`}>
                        {step.title}
                        {step.current && (
                          <span className="ml-2 text-[10px] bg-[#950E09] text-white px-2 py-0.2 rounded-full uppercase">Current Stage</span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">{step.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Items Summary & Invoice Action */}
            <div className="flex items-center justify-between pt-2">
              <div className="text-xs text-gray-600">
                Delivery to: <strong>{activeOrder.shippingAddress.city}, {activeOrder.shippingAddress.state}</strong>
              </div>

              <button
                onClick={() => onOpenInvoice(activeOrder)}
                className="px-4 py-2 rounded-xl bg-white border border-[#E09F55] text-[#950E09] hover:bg-[#E09F55]/10 text-xs font-bold flex items-center space-x-1.5 shadow-sm transition-all cursor-pointer"
              >
                <FileText className="w-4 h-4 text-[#E09F55]" />
                <span>View Tax Invoice</span>
              </button>
            </div>

          </div>
        )}

      </motion.div>
    </div>
  );
}
